import React, { useState, useEffect } from 'react';
import { Users, Download, Search, TrendingUp, IndianRupee, Star, Phone, Calendar, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { format } from 'date-fns';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => { fetchCustomers(); }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await api.getCustomers();
      setCustomers(res.data);
    } catch {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setExportLoading(true);
    try {
      const res = await api.exportCustomersCSV();
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customers-${format(new Date(), 'yyyy-MM-dd')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Customer list downloaded');
    } catch {
      toast.error('Failed to export');
    } finally {
      setExportLoading(false);
    }
  };

  const filtered = customers.filter(c => {
    const q = search.toLowerCase();
    return !q || c.name?.toLowerCase().includes(q) || c.phone?.includes(q);
  });

  const totalRevenue = customers.reduce((s, c) => s + (c.total_spend || 0), 0);
  const totalVisits = customers.reduce((s, c) => s + (c.total_visits || 0), 0);
  const avgSpend = customers.length ? totalRevenue / customers.length : 0;

  // Sort customers by tier
  const getTier = (spend) => {
    if (spend >= 5000) return { label: 'Gold', color: 'text-yellow-600 bg-yellow-50 border-yellow-200' };
    if (spend >= 2000) return { label: 'Silver', color: 'text-gray-500 bg-gray-50 border-gray-200' };
    return { label: 'Regular', color: 'text-blue-600 bg-blue-50 border-blue-200' };
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading customers...</div>;

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Customer Profiles</h2>
            <p className="text-gray-500 mt-1">Auto-built from billing records. Every customer who pays gets a profile.</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchCustomers}
              className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button
              onClick={handleExportCSV}
              disabled={exportLoading || customers.length === 0}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#E65100] hover:bg-[#D84800] text-white rounded-xl text-sm font-semibold transition disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {exportLoading ? 'Exporting...' : 'Export CSV'}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-blue-100 rounded-lg"><Users className="w-4 h-4 text-blue-600" /></div>
              <span className="text-sm text-gray-500">Total Customers</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{customers.length}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-100 rounded-lg"><IndianRupee className="w-4 h-4 text-green-600" /></div>
              <span className="text-sm text-gray-500">Total Revenue</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">₹{totalRevenue.toFixed(0)}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-orange-100 rounded-lg"><TrendingUp className="w-4 h-4 text-orange-600" /></div>
              <span className="text-sm text-gray-500">Total Visits</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">{totalVisits}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-purple-100 rounded-lg"><Star className="w-4 h-4 text-purple-600" /></div>
              <span className="text-sm text-gray-500">Avg Spend / Customer</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">₹{avgSpend.toFixed(0)}</p>
          </div>
        </div>

        {/* Export tip */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6 flex items-center gap-3">
          <Download className="w-4 h-4 text-amber-600 shrink-0" />
          <p className="text-sm text-amber-800">
            <span className="font-semibold">Export CSV</span> to import into WhatsApp Business, Google Contacts, or any CRM tool for marketing campaigns.
          </p>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or phone number..."
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E65100]"
          />
        </div>

        {/* Customer Table */}
        {customers.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-16 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">No customer profiles yet</p>
            <p className="text-sm text-gray-400 mt-1">Customer profiles are created automatically when you save a bill with a customer's name and phone number.</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {['Customer', 'Phone', 'Total Visits', 'Total Spend', 'Avg per Visit', 'Last Visit', 'Favourite Items', 'Tier'].map(h => (
                      <th key={h} className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((c) => {
                    const tier = getTier(c.total_spend || 0);
                    const avgPerVisit = c.total_visits > 0 ? (c.total_spend / c.total_visits) : 0;
                    return (
                      <tr key={c.id || c.phone} className="hover:bg-gray-50 transition">
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-[#E65100]/10 flex items-center justify-center text-[#E65100] font-bold text-sm flex-shrink-0">
                              {c.name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <span className="font-medium text-gray-900">{c.name || '—'}</span>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-1.5 text-sm text-gray-600">
                            <Phone className="w-3.5 h-3.5 text-gray-400" />
                            {c.phone}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span className="text-lg font-bold text-gray-900">{c.total_visits}</span>
                        </td>
                        <td className="px-5 py-4">
                          <span className="text-lg font-semibold text-[#E65100]">₹{(c.total_spend || 0).toFixed(0)}</span>
                        </td>
                        <td className="px-5 py-4">
                          <span className="text-sm text-gray-600">₹{avgPerVisit.toFixed(0)}</span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-1.5 text-sm text-gray-500">
                            <Calendar className="w-3.5 h-3.5" />
                            {c.last_visit ? format(new Date(c.last_visit), 'dd MMM yyyy') : '—'}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex flex-wrap gap-1">
                            {(c.favourite_items || []).slice(0, 3).map((item, i) => (
                              <span key={i} className="text-xs bg-orange-50 text-orange-700 border border-orange-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                                {item.name}
                              </span>
                            ))}
                            {!c.favourite_items?.length && <span className="text-xs text-gray-400">—</span>}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`text-xs font-semibold border px-2.5 py-1 rounded-full ${tier.color}`}>
                            {tier.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filtered.length === 0 && search && (
              <div className="py-10 text-center text-gray-400 text-sm">No customers match "{search}"</div>
            )}

            <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 text-xs text-gray-400 flex justify-between">
              <span>Showing {filtered.length} of {customers.length} customers</span>
              <span>Sorted by total spend (highest first)</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Customers;
